import React from 'react';
import { IconButton, Tooltip } from '@material-ui/core';
import { Create as EditIcon, Description as RegistrationIcon } from '@material-ui/icons';
import Table, {
  tempFilteredRows,
  columnType,
} from '../../components/ClearingTable/Table';
import Button from '../../components/Buttons/Button';
import AutoCompleteCorrespondent from '../../components/AutoComplete/AutoCompleteCorrespondent';
import AutoCompleteAccountNo from '../../components/AutoComplete/AutoCompleteAccountNo';
import AutoCompleteAccountName from '../../components/AutoComplete/AutoCompleteAccountName';
import AutoCompleteMasterAccountNo from '../../components/AutoComplete/AutoCompleteMasterAccountNo';
import AutoCompleteBranch from '../../components/AutoComplete/AutoCompleteBranch';
import AutoCompleteRep from '../../components/AutoComplete/AutoCompleteRep';
import SelectSystemCode from '../../components/Dropdown/SelectSystemCode';
import Text from '../../components/Typography/Text';
import ClientAccountModal from '../../components/Modals/ClientAccountModal';
import { protoTimeSpanObjectToString } from '../../services/ConvertService';
import {
  notifySuccess,
  notifyInfo,
  notifyError,
} from '../../components/Messages/Notification';
import { updateClient, listClientAccount } from '../../services/ClientAccountService';
import QueryParam from '../../services/QueryParamService';
import {
  CustomBodyRenderAlign,
  CustomBodyRenderPercentage,
} from '../../components/Table/CustomBodyRender';
import { formatPbDate, formatPbDateTimeWithMillis } from '../../lib/fmt';

export default function ClientAccount() {
  const [pagination, setPagination] = React.useState({});
  const [totalRows, setTotalRows] = React.useState(0);
  const [searchDataCsv, setSearchDataCsv] = React.useState([]);

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [openModal, setOpenModal] = React.useState(false);
  const [rowData, setRowData] = React.useState({});
  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      correspondent: '',
      branch: '',
      accountNo: '',
      masterAccountNo: '',
      rep: '',
      accountName: '',
      broker: '',
      status: '',
    })
  );
  const options = {
    serverSide: true,
    selectableRows: 'none',
    responsive: 'standard',
  };

  const columns = [
    {
      name: 'actions',
      label: 'Actions',
      type: columnType.buttons,
      options: {
        customBodyRenderLite: (dataIndex) => {
          const onboardingPercent = parseInt(
            tempFilteredRows[dataIndex]?.onboardingPercent
          );
          const status = tempFilteredRows[dataIndex]?.status;
          return (
            <div align={'left'} className="grd-row" style={{ margin: 0, padding: 0 }}>
              <div align={'center'} className="grd-cell-none">
                <Tooltip title="Edit" arrow>
                  <IconButton
                    aria-label="view"
                    onClick={() => {
                      handleOpen(tempFilteredRows[dataIndex]);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                {onboardingPercent < 100 &&
                  ['In Progress', 'Pending', 'For Review'].includes(status) && (
                    <>
                      <Tooltip title="View Registration" arrow>
                        <IconButton
                          aria-label="view"
                          onClick={() => {
                            window.open(
                              '/app/registration/account/' +
                                tempFilteredRows[dataIndex]?.accountId,
                              '_blank'
                            );
                          }}
                        >
                          <RegistrationIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
              </div>
            </div>
          );
        },
        sort: false,
      },
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'branch',
      label: 'Branch',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'onboardingPercent',
      label: 'Onboarding Percent',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderPercentage,
      },
      type: 'percentage',
    },
    {
      name: 'accountNo',
      label: 'Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'rep',
      label: 'Rep',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountName',
      label: 'Account Name',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'broker',
      label: 'Broker',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'privacyConsent',
      label: 'Privacy Consent',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'taxCountry',
      label: 'Tax Country',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'emailAddress',
      label: 'Email Address',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        customBodyRenderLite: (dataIndex) => {
          const data = tempFilteredRows[dataIndex]?.emailAddress?.split(',');
          return data?.map((d, index) => <div key={index}>{d}</div>);
        },
      },
    },
    {
      name: 'accountType',
      label: 'Account Type',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'marginType',
      label: 'Margin Type',
      options: {
        display: false,
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'createdBy',
      label: 'Created By',
      options: {
        display: false,
      },
    },
    {
      name: 'createdAt',
      label: 'Created At',
      options: {
        display: false,
        customBodyRenderLite: (dataIndex) => {
          return protoTimeSpanObjectToString(
            rows[dataIndex]?.createdAt,
            'MM/DD/YYYY hh:mm:ss a'
          );
        },
      },
    },
    {
      name: 'modifiedAt',
      label: 'Modified At',
      options: {
        display: false,
        customBodyRenderLite: (dataIndex) => {
          return protoTimeSpanObjectToString(
            rows[dataIndex]?.modifiedAt,
            'MM/DD/YYYY hh:mm:ss a'
          );
        },
      },
    },
    {
      name: 'modifiedBy',
      label: 'Modified By',
      options: {
        display: false,
      },
    },
  ];

  const handleSearchDataChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({ ...searchData, [input.name]: input.value });
  };

  const handleSearch = async (paginationChange, fromClose) => {
    try {
      setLoading(true);

      QueryParam.set(searchData);
      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };

      const data = await listClientAccount(searchData, paginationCopy);
      setSearchDataCsv(searchData);
      setRows(data.clientsList);

      paginationCopy.count = data.totalRows;
      setPagination(paginationCopy);
      setTotalRows(data.totalRows);

      if (paginationChange !== true && fromClose !== true) {
        notifyInfo(
          data.clientsList.length > 0
            ? data.totalRows + ' search result(s)'
            : 'No records found.'
        );
      }
    } catch (error) {
      setLoading(false);
      notifyError(error.message);
      console.error(error);
      return;
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async (data) => {
    setRowData(data);
    setOpenModal(true);
  };

  const handleCloseModal = async (data, isEdit) => {
    if (!data) {
      setOpenModal(false);
      return;
    }

    let valid = true;

    if (!data.correspondent || data.correspondent.replaceAll(' ', '').length !== 4) {
      notifyError('Correspondent should have 4 characters.');
      valid = false;
    }
    if (!data.accountNo) {
      notifyError('Account No is required.');
      valid = false;
    }
    if (!data.accountName) {
      notifyError('Account name is required.');
      valid = false;
    }
    if (!data.broker) {
      notifyError('Broker is required.');
      valid = false;
    }
    if (!data.legalEntity) {
      notifyError('Legal entity is required.');
      valid = false;
    }
    if (!data.accountType) {
      notifyError('Account type is required.');
      valid = false;
    }
    if (!data.marginType) {
      notifyError('Margin type is required.');
      valid = false;
    }
    if (!data.privacyConsent) {
      notifyError('Privacy Consent is required.');
      valid = false;
    }
    if (!data.tefra) {
      notifyError('Tefra is required.');
      valid = false;
    }
    if (!data.taxCountry) {
      notifyError('Tax Country is required.');
      valid = false;
    }

    if (!data.w8w9) {
      notifyError('W8W9 is required.');
      valid = false;
    }
    if (data.status === 'Active' && !data.activatedAt) {
      notifyError('Activated At is required.');
      valid = false;
    }
    if (!data.status) {
      notifyError('Status is required.');
      valid = false;
    }

    if (data.ownership === true) {
      if (!data.stockOwnership) {
        notifyError('Stock Symbol is required.');
        valid = false;
      }
    }

    if (data.brokerMember === true) {
      if (!data.brokerName) {
        notifyError('Company Name is required.');
        valid = false;
      }

      if (!data.affiliatePerson) {
        notifyError('Person Affiliated is required.');
        valid = false;
      }

      if (!data.affiliatePersonPosition) {
        notifyError('Position of Affiliated Person is required.');
        valid = false;
      }
    }

    if ([0, '0'].includes(data.multiplier)) {
      notifyError('0 Multiplier is not allowed. Min should be 1.');
      valid = false;
    }

    if (!valid) {
      return;
    }

    setLoading({ ...loading, save: true });

    try {
      if (isEdit) {
        //EDIT
        await updateClient(data).then((res) => {
          const rowsCopy = [...rows];
          const index = rows.indexOf(rowData);
          rowsCopy[index] = res.client;
          setRows(rowsCopy);
          notifySuccess('Client Account has been updated.');
        });
      }

      setOpenModal(false);
    } catch (error) {
      setLoading({ ...loading, save: false });
      notifyError(error.message);
    }

    setLoading({ ...loading, save: false });

    handleSearch(false, true);
  };

  const getCsvData = async () => {
    if (totalRows === 0) {
      return [];
    }
    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: totalRows,
    };

    const data = await listClientAccount(searchDataCsv, paginationCopy);
    const csvData = data.clientsList.map((data) => {
      data.activatedAt = formatPbDateTimeWithMillis(data.activatedAt);
      data.pendingClosureAt = formatPbDateTimeWithMillis(data.pendingClosureAt);
      data.webTermConditions = formatPbDateTimeWithMillis(data.webTermConditions);
      data.disclosureStatement = formatPbDateTimeWithMillis(data.disclosureStatement);
      data.feeSchedule = formatPbDateTimeWithMillis(data.feeSchedule);
      data.investmentObjectiveDate = formatPbDate(data.investmentObjectiveDate);
      data.w8w9ReceivedDate = formatPbDate(data.w8w9ReceivedDate);
      data.createdAt = formatPbDateTimeWithMillis(data.createdAt);
      data.modifiedAt = formatPbDateTimeWithMillis(data.modifiedAt);
      data.lastActivityDate = formatPbDate(data.lastActivityDate);
      delete data.imgSignature;

      return data;
    });

    return csvData;
  };

  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Account Setup" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading}
              loading={loading}
              type="search"
              label={loading ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <AutoCompleteCorrespondent
              isAccessibleOnly={true}
              type="Client"
              name="correspondent"
              value={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteBranch
              isAccessibleOnly={true}
              name="branch"
              value={searchData.branch}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountNo
              isAccessibleOnly={true}
              isAllStatus={true}
              type={'Client'}
              name="accountNo"
              value={searchData.accountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteMasterAccountNo
              isAccessibleOnly={true}
              isAllStatus={true}
              type={'Client'}
              name="masterAccountNo"
              value={searchData.masterAccountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteRep
              isAccessibleOnly={true}
              name="rep"
              value={searchData.rep}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountName
              isAccessibleOnly={true}
              isAllStatus={true}
              type={'Client'}
              name="accountName"
              value={searchData.accountName}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              name="broker"
              label="Broker"
              placeholder="Broker"
              type="Broker"
              value={searchData.broker}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              name="status"
              label="Status"
              placeholder="Status"
              type="Status"
              subType="Client Setup"
              value={searchData.status}
              onChange={handleSearchDataChange}
            />
          </div>
        </div>
      </div>
      <div className="mt-20">
        <Table
          title={'Client Account'}
          componentName={'ClientSiteClientAccount'}
          data={rows}
          columns={columns}
          options={options}
          getCsvData={getCsvData}
          pagination={pagination}
          setPagination={setPagination}
          onSearch={handleSearch}
        />
      </div>
      <ClientAccountModal
        openModal={openModal}
        onClose={handleCloseModal}
        value={rowData}
      />
    </form>
  );
}
